import { Button, Card, Icon } from '@salesforce/design-system-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { PageMeta } from '../../../shared/components';
import { NoMatchPage } from '../../../shared/pages/NoMatchPage';
import { IconCategory } from '../../../shared/types/Enum';
import {
  QualityFormulaStoreProvider,
  useQualityFormulaStoreApi,
} from '../../quality-formulas/store/QualityFormulaStoreProvider';
import { ContractDetailsPanel } from '../components/ContractDetailsPanel';
import { LineItemSection } from '../components/LineItemSection';
import { LineItemSidebar } from '../components/LineItemSidebar';
import { QualityAdjustmentSection } from '../components/QualityAdjustmentSection';
import {
  CONTRACT_NUMBER,
  getContractPath,
  getLineItemPath,
  type LineItemEntry,
  type LineQualityConfig,
} from '../lineItemData';
import '../lineItemEditor.css';

const LineItemForm: React.FC<{ item: LineItemEntry; onSave: () => void }> = ({ item, onSave }) => (
  <Card
    className="line-item-editor-main slds-card_boundary"
    headerActions={
      <Button
        className="slds-button_small"
        iconCategory={IconCategory.Utility}
        iconName="save"
        iconPosition="left"
        label="Save Line Item"
        onClick={onSave}
        title="Saves this line in the local demo only"
        variant="brand"
      />
    }
    heading={
      <span className="slds-grid slds-grid_vertical-align-center" style={{ gap: 'var(--slds-g-spacing-2)' }}>
        <Icon category={IconCategory.Standard} name="product" size="small" />
        <span className="slds-grid slds-grid_vertical slds-p-vertical_xx-small">
          <span className="slds-text-heading_small">
            Line {item.lineNumber} · {item.product}
          </span>
          <span className="slds-text-body_small slds-text-color_weak">Configure the line item</span>
        </span>
      </span>
    }
  >
    <div className="line-item-editor-sections">
      <LineItemSection iconName="settings" summary={`Product · ${item.product}`} title="Product">
        <p className="slds-text-body_small slds-text-color_weak">Product fields will be added here.</p>
      </LineItemSection>
      <LineItemSection iconName="currency" summary={item.priceSummary} title="Price">
        <p className="slds-text-body_small slds-text-color_weak">Price fields will be added here.</p>
      </LineItemSection>
      <LineItemSection iconName="date_input" summary={item.shipmentSummary} title="Estimated Shipment">
        <p className="slds-text-body_small slds-text-color_weak">Shipment fields will be added here.</p>
      </LineItemSection>
      <QualityAdjustmentSection />
      <LineItemSection iconName="edit" summary="No notes added" title="Notes to Print on Customer Documents">
        <p className="slds-text-body_small slds-text-color_weak">Notes fields will be added here.</p>
      </LineItemSection>
    </div>
  </Card>
);

interface LineItemFooterProps {
  item: LineItemEntry;
  onCancel: () => void;
  onSave: () => void;
  onSaveAndAdd: () => void;
}

const LineItemFooter: React.FC<LineItemFooterProps> = ({ item, onCancel, onSave, onSaveAndAdd }) => (
  <footer className="line-item-editor-footer">
    <div className="line-item-editor-footer-inner">
      <span className="slds-text-body_small slds-text-color_weak">
        {item.id === 'new' ? `Line ${item.lineNumber} · Unsaved draft` : `Line ${item.lineNumber} · Demo session`}
      </span>
      <div className="line-item-editor-footer-actions">
        <Button
          className="slds-button_small"
          iconCategory={IconCategory.Utility}
          iconName="close"
          iconPosition="left"
          label="Cancel"
          onClick={onCancel}
          variant="neutral"
        />
        <Button
          className="slds-button_small"
          iconCategory={IconCategory.Utility}
          iconName="save"
          iconPosition="left"
          label="Save Line"
          onClick={onSave}
          title="Saves this line in the local demo only"
          variant="outline-brand"
        />
        <Button
          className="slds-button_small"
          disabled={item.id !== 'new'}
          iconCategory={IconCategory.Utility}
          iconName="save"
          iconPosition="left"
          label="Save & Add Line"
          onClick={onSaveAndAdd}
          title={item.id === 'new' ? 'Saves this line in the local demo only' : 'No unsaved line to save'}
          variant="brand"
        />
        {item.id !== 'new' ? (
          <Button
            className="slds-button_small"
            disabled
            iconCategory={IconCategory.Utility}
            iconName="save"
            iconPosition="left"
            label="Save & Clone"
            title="Cloning is not connected yet"
            variant="outline-brand"
          />
        ) : null}
      </div>
    </div>
  </footer>
);

const ContractFooter: React.FC<{ contractId: string }> = ({ contractId }) => (
  <footer className="line-item-editor-footer">
    <div className="line-item-editor-footer-inner">
      <span className="slds-text-body_small slds-text-color_weak">{contractId} · Contract Entry</span>
      <div className="line-item-editor-footer-actions">
        <Button
          className="slds-button_small"
          disabled
          iconCategory={IconCategory.Utility}
          iconName="save"
          iconPosition="left"
          label="Save Contract"
          title="Contract fields and save behavior are not connected yet"
          variant="brand"
        />
      </div>
    </div>
  </footer>
);

interface LineItemWorkspaceProps {
  contractId: string;
  item: LineItemEntry;
  items: LineItemEntry[];
  onCancel: () => void;
  onDeleteItem: (itemId: string) => void;
  onDuplicateItem: (item: LineItemEntry) => void;
  onSave: (item: LineItemEntry, qualityConfig: LineQualityConfig, addAnother?: boolean) => void;
}

const LineItemWorkspaceContent: React.FC<LineItemWorkspaceProps> = ({
  contractId,
  item,
  items,
  onCancel,
  onDeleteItem,
  onDuplicateItem,
  onSave,
}) => {
  const store = useQualityFormulaStoreApi();
  const save = (addAnother = false) => {
    const { specStates, visibleSpecTypes } = store.getState();
    onSave(item, { specStates, visibleSpecTypes }, addAnother);
  };

  return (
    <section className="line-item-editor">
      <PageMeta
        description="Configure a contract line item and its quality adjustment terms."
        title="Line Item Editor"
      />
      <div className="line-item-editor-page">
        <div className="line-item-editor-layout">
          <LineItemSidebar
            contractId={contractId}
            draftItem={item.id === 'new' ? item : undefined}
            items={items}
            onDeleteItem={onDeleteItem}
            onDuplicateItem={onDuplicateItem}
            selectedItemId={item.id}
          />
          <LineItemForm
            item={item}
            onSave={() => {
              save();
            }}
          />
        </div>
      </div>
      <LineItemFooter
        item={item}
        onCancel={onCancel}
        onSave={() => {
          save();
        }}
        onSaveAndAdd={() => {
          save(true);
        }}
      />
    </section>
  );
};

export const ContractLineItemEditorPage: React.FC = () => {
  const { '*': routeSuffix = '', contractId = CONTRACT_NUMBER } = useParams();
  const navigate = useNavigate();
  const [lineItems, setLineItems] = useState<LineItemEntry[]>([]);
  const routeMatch = /^line-items\/([^/]+)$/.exec(routeSuffix);
  const lineItemId = routeMatch?.[1];
  const isContractView = routeSuffix === '';
  const nextLineNumber = Math.max(0, ...lineItems.map(line => line.lineNumber)) + 1;
  const draft: LineItemEntry = {
    id: 'new',
    lineNumber: nextLineNumber,
    priceSummary: 'Product and quality specs not saved yet',
    product: 'New Draft',
    shipmentSummary: 'Shipment not configured',
  };
  let item: LineItemEntry | undefined;
  if (!isContractView) {
    item = lineItemId === 'new' ? draft : lineItems.find(line => line.id === lineItemId);
  }
  const saveLine = (line: LineItemEntry, qualityConfig: LineQualityConfig, addAnother = false) => {
    if (line.id !== 'new') {
      setLineItems(current =>
        current.map(existing => (existing.id === line.id ? { ...existing, qualityConfig } : existing)),
      );
      return;
    }
    const savedLine: LineItemEntry = {
      ...line,
      id: `line-${line.lineNumber}`,
      priceSummary: 'Price details not configured',
      product: 'New Line',
      qualityConfig,
      shipmentSummary: 'Shipment details not configured',
    };
    setLineItems(current => [...current, savedLine]);
    void navigate(getLineItemPath(addAnother ? 'new' : savedLine.id, contractId));
  };

  const deleteLineItem = (itemId: string) => {
    setLineItems(current => current.filter(line => line.id !== itemId));
    if (lineItemId === itemId) {
      void navigate(getContractPath(contractId));
    }
  };

  const duplicateLineItem = (source: LineItemEntry) => {
    const lineNumber = Math.max(0, ...lineItems.map(line => line.lineNumber)) + 1;
    const duplicate: LineItemEntry = {
      ...source,
      id: `line-${lineNumber}`,
      lineNumber,
      qualityConfig: source.qualityConfig ? structuredClone(source.qualityConfig) : undefined,
    };
    setLineItems(current => [...current, duplicate]);
    void navigate(getLineItemPath(duplicate.id, contractId));
  };

  if (isContractView) {
    return (
      <section className="line-item-editor">
        <PageMeta description="Review contract details and line items in one workspace." title="Contract Entry" />
        <div className="line-item-editor-page">
          <div className="line-item-editor-layout">
            <LineItemSidebar
              contractId={contractId}
              items={lineItems}
              onDeleteItem={deleteLineItem}
              onDuplicateItem={duplicateLineItem}
            />
            <ContractDetailsPanel />
          </div>
        </div>
        <ContractFooter contractId={contractId} />
      </section>
    );
  }

  if (!item) return <NoMatchPage />;

  return (
    <QualityFormulaStoreProvider
      initialSpecStates={item.qualityConfig?.specStates}
      initialVisibleSpecTypes={item.qualityConfig?.visibleSpecTypes}
      key={`${contractId}:${item.id}`}
    >
      <LineItemWorkspaceContent
        contractId={contractId}
        item={item}
        items={lineItems}
        onCancel={() => {
          void navigate(getContractPath(contractId));
        }}
        onDeleteItem={deleteLineItem}
        onDuplicateItem={duplicateLineItem}
        onSave={saveLine}
      />
    </QualityFormulaStoreProvider>
  );
};
